import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Camera, Image as ImageIcon, Loader2, Layers } from "lucide-react";
import { EQUIPMENT_TYPES } from "@/lib/status";

const EMPTY = { equipment_type: "", brand: "", model: "", serial_number: "", capacity_btu: "", voltage: "", location: "", photo: "" };
const FIELDS = [
  ["brand", "Fabricante"], ["model", "Modelo"], ["serial_number", "Nº de série"],
  ["capacity_btu", "Capacidade (BTU)"], ["voltage", "Tensão"], ["location", "Local / Sala"],
];

function EquipmentRow({ idx, item, onChange, onRemove, disabled }) {
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => onChange({ ...item, [k]: v });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.path);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="rounded-md border border-slate-200 p-2.5 space-y-2 bg-white" data-testid={`os-eq-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-[#0a2a6c]">Aparelho {idx + 1}</span>
        {!disabled && (
          <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-600" data-testid={`os-eq-remove-${idx}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <Select value={item.equipment_type || "none"} disabled={disabled} onValueChange={(v) => set("equipment_type", v === "none" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid={`os-eq-type-${idx}`}><SelectValue placeholder="Tipo" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Tipo não informado</SelectItem>
          {EQUIPMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(([k, l]) => (
          <Input key={k} className="h-9" placeholder={l} value={item[k] || ""} disabled={disabled}
            onChange={(e) => set(k, e.target.value)} data-testid={`os-eq-${k}-${idx}`} />
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className={`h-12 w-12 rounded-md border overflow-hidden flex items-center justify-center bg-slate-50 ${item.photo ? "border-slate-200" : "border-amber-300"}`}>
          {item.photo ? <img src={fileUrl(item.photo)} alt="Aparelho" className="h-full w-full object-cover" data-testid={`os-eq-photo-preview-${idx}`} />
            : uploading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <Camera className="h-4 w-4 text-slate-300" />}
        </div>
        {!disabled && (
          <>
            <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-slate-200 text-xs px-2.5 py-1.5 hover:bg-slate-50">
              <Camera className="h-3.5 w-3.5" /> Tirar foto
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid={`os-eq-photo-camera-${idx}`} />
            </label>
            <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-slate-200 text-xs px-2.5 py-1.5 hover:bg-slate-50">
              <ImageIcon className="h-3.5 w-3.5" /> Galeria
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`os-eq-photo-input-${idx}`} />
            </label>
          </>
        )}
        {!item.photo && <span className="text-[11px] text-amber-600">Foto obrigatória para encerrar</span>}
      </div>
    </div>
  );
}

export default function OsEquipmentsList({ items, onChange, disabled, onSaveOnly }) {
  const update = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="os-equipments-section">
      <Label className="text-sm flex items-center gap-1.5"><Layers className="h-4 w-4 text-[#1e7be0]" /> Equipamentos desta OS</Label>
      <p className="text-[11px] text-slate-500">Todos os aparelhos atendidos neste chamado — edite ou exclua quando quiser. Cada um precisa de foto para encerrar.</p>
      {items.map((it, i) => (
        <EquipmentRow key={i} idx={i} item={it} disabled={disabled}
          onChange={(v) => update(i, v)} onRemove={() => onChange(items.filter((_, j) => j !== i))} />
      ))}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5 w-full" onClick={() => onChange([...items, { ...EMPTY }])}
          data-testid="os-eq-add-button">
          <Plus className="h-4 w-4" /> Adicionar equipamento
        </Button>
      )}
      {onSaveOnly && (
        <Button type="button" size="sm" className="w-full bg-blue-600 hover:bg-blue-700" onClick={onSaveOnly}
          data-testid="os-eq-save-only-button">
          Salvar equipamentos
        </Button>
      )}
    </div>
  );
}
